import { keymap } from '@codemirror/view';
import CodeMirror, { basicSetup } from '@uiw/react-codemirror';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DSPlayIcon } from '@design-system/icons';
import { BasicModal } from '@DesignSystem/modals';
import { Button } from '@ds';
import {
  usePanelLibraryCreateTemplateRevisionMutation,
  usePanelLibraryTemplateCode,
  usePanelLibraryTemplates
} from '@mpm-druid/api';
import {
  DEFAULT_PANEL_LIBRARY_TEMPLATE,
  DEFAULT_PYTHON_PANEL_CODE,
  DEFAULT_PYTHON_PANEL_NAME
} from '@mpm-druid/constants';
import { PythonPanelConfig } from '@mpm-druid/types';
import { getDefaultPythonCodeExtensions } from '@shared';
import {
  SubmitPanelPayload,
  useSubmitPanelInterceptor
} from '../SubmitPanelContext';
import { PythonTemplateSelect } from './PythonTemplateSelect';

import classes from './PythonCode.module.scss';

const DEBOUNCE_DELAY = 300;

export function PythonCode({
  config,
  name,
  onChange,
  onNameChange
}: {
  config: PythonPanelConfig;
  name: string;
  onChange: (
    update: (prev: PythonPanelConfig) => PythonPanelConfig,
    shouldApply?: boolean
  ) => void;
  onNameChange: (name: string) => void;
}) {
  const [code, setCode] = useState<string>(config.code || '');
  const [selectedRevisionId, setSelectedRevisionId] = useState(
    config.panelLibraryRevisionId
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    config.panelLibraryId
  );

  const {
    isLoading: isCreatingTemplateRevision,
    mutateAsync: createTemplateRevisionAsync
  } = usePanelLibraryCreateTemplateRevisionMutation();
  const { data: templates } = usePanelLibraryTemplates();
  const {
    data: latestCode,
    isLoading: isLoadingLatestCode
  } = usePanelLibraryTemplateCode(
    {
      revisionId: config.panelLibraryRevisionId,
      templateId: config.panelLibraryId
    },
    {
      keepPreviousData: true,
      onError: () => {
        setSelectedRevisionId(undefined);
        setSelectedTemplateId(undefined);
      }
    }
  );

  const shouldIntercept = useCallback(
    () => !!selectedTemplateId && code !== latestCode,
    [code, latestCode, selectedTemplateId]
  );

  const {
    cancel,
    confirm,
    replace,
    payload: savingPanelPayload,
    payloadRef: savingPanelPayloadRef
  } = useSubmitPanelInterceptor(shouldIntercept);

  const allTemplates = useMemo(() => {
    return [DEFAULT_PANEL_LIBRARY_TEMPLATE, ...(templates || [])];
  }, [templates]);

  const handleChangeCode = useCallback(
    (newCode: string, shouldApply?: boolean) => {
      if (selectedTemplateId && newCode === latestCode) {
        onChange(() => ({
          panelLibraryId: selectedTemplateId,
          panelLibraryRevisionId: selectedRevisionId
        }));
      } else {
        onChange(() => ({ code: newCode }), shouldApply);
      }
    },
    [latestCode, onChange, selectedRevisionId, selectedTemplateId]
  );

  const debouncedChangeCode = useMemo(() => {
    return debounce(handleChangeCode, DEBOUNCE_DELAY);
  }, [handleChangeCode]);

  useEffect(() => {
    if (config.panelLibraryId && latestCode) {
      setCode(latestCode);
    }
  }, [config.panelLibraryId, latestCode]);

  const handleSelectTemplate = (
    templateId: string | null,
    revisionId?: string
  ) => {
    const oldTemplate = allTemplates.find(t => t.id === selectedTemplateId);
    const newTemplate = allTemplates.find(t => t.id === templateId);

    const hasTemplateNameChanged = oldTemplate?.id
      ? oldTemplate.name !== name
      : DEFAULT_PYTHON_PANEL_NAME !== name;

    if (!hasTemplateNameChanged) {
      onNameChange(
        newTemplate && templateId ? newTemplate.name : DEFAULT_PYTHON_PANEL_NAME
      );
    }

    if (!templateId) {
      setSelectedRevisionId(undefined);
      setSelectedTemplateId(undefined);
      setCode(DEFAULT_PYTHON_PANEL_CODE);
      onChange(() => ({ code: DEFAULT_PYTHON_PANEL_CODE }));
    } else {
      setSelectedRevisionId(revisionId);
      setSelectedTemplateId(templateId);
      onChange(() => ({
        panelLibraryId: templateId,
        panelLibraryRevisionId: revisionId
      }));
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplateId) {
      return;
    }

    try {
      await createTemplateRevisionAsync({
        code,
        templateId: selectedTemplateId
      });
    } finally {
      // This handles the case where the user manually click into "Update template" button
      // and also in case it is already saving the template revision on add/update panel
      if (savingPanelPayloadRef.current) {
        savePanel(savingPanelPayloadRef.current);
      } else {
        onChange(() => ({ panelLibraryId: selectedTemplateId }));
      }
    }
  };

  const submitKeymap = keymap.of([
    {
      key: 'Mod-Enter',
      run: () => {
        debouncedChangeCode(code);
        return true;
      },
      stopPropagation: true
    },
    {
      key: 'Mod-s',
      run: () => {
        handleSaveTemplate();
        return true;
      },
      preventDefault: true
    }
  ]);

  const selectedTemplateName = allTemplates.find(
    template => template.id === selectedTemplateId
  )?.name;

  const savePanel = useCallback(
    (payload: SubmitPanelPayload) => {
      // Always `false` condition, type guard
      if (payload.panel.data.type !== 'python' || !selectedTemplateId) {
        confirm();
        return;
      }

      replace({
        ...payload.panel,
        data: {
          ...payload.panel.data,
          config: {
            panelLibraryId: selectedTemplateId
          }
        }
      });
    },
    [confirm, replace, selectedTemplateId]
  );

  return (
    <>
      <div className={classes.container}>
        <div className={classes.actions}>
          <div className={classes.actionsLeft}>
            {templates ? (
              <>
                <PythonTemplateSelect
                  code={code}
                  templates={allTemplates}
                  selectedTemplateId={selectedTemplateId}
                  selectedRevisionId={selectedRevisionId}
                  onSelectTemplate={handleSelectTemplate}
                />
                <Button
                  data-test="save-python-template-button"
                  disabled={
                    !selectedTemplateId ||
                    isLoadingLatestCode ||
                    isCreatingTemplateRevision ||
                    code === latestCode
                  }
                  onClick={handleSaveTemplate}
                  type="secondary"
                >
                  Save
                </Button>
              </>
            ) : null}
          </div>
          <Button
            disabled={!code}
            onClick={() => handleChangeCode(code)}
            PrefixIcon={<DSPlayIcon />}
          >
            Run code
          </Button>
        </div>
        <div
          className={cx(classes.codeContainer, {
            [classes.isCodeLoading]: isLoadingLatestCode
          })}
        >
          <CodeMirror
            autoFocus
            basicSetup={false}
            className={classes.pythonCodeMirror}
            extensions={[
              submitKeymap,
              basicSetup(),
              ...getDefaultPythonCodeExtensions()
            ]}
            onChange={newCode => {
              setCode(newCode);
              debouncedChangeCode(newCode, false);
            }}
            editable={!isLoadingLatestCode}
            value={code}
          />
        </div>
      </div>
      {savingPanelPayload ? (
        <BasicModal
          open
          dataTest="template-unsaved-changes-modal"
          onClose={cancel}
          title="You have unsaved changes in the template"
          content={
            <div>
              <div>
                You have unsaved changes in the panel template{' '}
                <strong>{selectedTemplateName}</strong>. Updating the template
                will affect all the panels that use this template.
              </div>
            </div>
          }
          primaryButtonText="Update template"
          isPrimaryButtonDisabled={isCreatingTemplateRevision}
          secondaryButtonText={
            savingPanelPayload.action === 'create'
              ? 'Only create this panel'
              : 'Only update this panel'
          }
          isSecondaryButtonDisabled={isCreatingTemplateRevision}
          onSecondaryButtonClick={confirm}
          onPrimaryButtonClick={async () => {
            await handleSaveTemplate();
          }}
        />
      ) : null}
    </>
  );
}
